import { Cause, Effect, Stream } from "effect";
import {
	HttpClient,
	type HttpClientError,
	HttpClientRequest,
} from "effect/unstable/http";

import { apiBase, statusError } from "../api/client";
import { ApiError, toMessage } from "../api/errors";
import { buildUrl } from "../api/url";
import { ClerkTokenService } from "../auth/tokenService";
import type { Api } from "../pages/shared";
import { Message, type PendingRequest } from "./aiChat";

const describe = (error: unknown): string =>
	error instanceof Error ? error.message : String(error);

type Body = Stream.Stream<Message, HttpClientError.HttpClientError>;

const failed = (id: string, error: string): Body =>
	Stream.succeed(Message.StreamFailed({ id, error }));

/**
 * `/chat/stream` as a Stream of chat Messages (KTD5, KTD6): one
 * `ChunkReceived` per decoded body chunk, then `StreamEnded`; any failure —
 * a non-OK status before streaming (mapped like every other request), a
 * missing session, a transport error mid-body — ends with `StreamFailed`.
 * The root gates it on the page's `pendingRequest` (see `app/update.ts`).
 */
export const streamChat = ({
	id,
	text,
	history,
}: PendingRequest): Stream.Stream<Message, never, Api> =>
	Stream.unwrap(
		Effect.gen(function* () {
			const client = yield* HttpClient.HttpClient;
			const send = Effect.gen(function* () {
				const token = yield* (yield* ClerkTokenService).getToken;
				const request = HttpClientRequest.make("POST")(
					buildUrl(apiBase(), "/chat/stream"),
					{ headers: { Authorization: `Bearer ${token}` } },
				).pipe(
					HttpClientRequest.bodyJsonUnsafe({
						message: text,
						messages: history,
					}),
				);
				return yield* client.execute(request);
			});
			// As `api/client.ts`: a 401 is retried once with a re-read token (AE2).
			const first = yield* send;
			const response = first.status === 401 ? yield* send : first;
			if (response.status === 401) {
				return failed(id, toMessage(ApiError.Unauthenticated()));
			}
			if (response.status >= 400) {
				const body = yield* response.json.pipe(
					Effect.orElseSucceed(() => ({})),
				);
				return failed(id, toMessage(statusError(response.status, body)));
			}
			const body: Body = response.stream.pipe(
				Stream.decodeText(),
				Stream.map(
					(chunk): Message => Message.ChunkReceived({ id, text: chunk }),
				),
				Stream.concat(Stream.succeed<Message>(Message.StreamEnded({ id }))),
			);
			return body;
		}),
	).pipe(
		Stream.catchCause((cause) =>
			Stream.succeed(
				Message.StreamFailed({ id, error: describe(Cause.squash(cause)) }),
			),
		),
	);
